import React, { useCallback } from 'react'
import { Box, Stack, Typography, styled } from '@mui/material'
import { RawFileMessage } from '@weblite/ts-action/message'
import { getFileUrl, openFile } from '@weblite/file'
import { ActionHandler } from '$common/action-handler'
import { FileMessageIcon } from '$common/icons/file-message'
import { useContentFileStatus } from '$common/react-hooks/message'
import { formatFileSize, getFileExtension, getFileTitle } from '$func/file'
import { IS_IOS } from '$func/environmentInfo'

const Container = styled(Stack)`
  position: relative;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;

  padding: 10px 0;
  box-sizing: border-box;
  overflow: hidden;
`

const TextsContainer = styled(Box)`
  flex-grow: 1;
  margin-right: 10px;
  overflow: hidden;
`

const FileMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

export function File({
  attachment,
  selected,
  selectOnTap,
}: {
  attachment: RawFileMessage['attachment']
  selected: boolean
  selectOnTap: boolean
}) {
  const { fileId, filename, size } = attachment

  const {
    state,
    percentage,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId: null, messageType: 'FILE' })

  const isInProgress = state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } = formatFileSize(
    Math.round(size * (percentage / 100)),
    fileSizeUnit,
    false,
  )

  const fileTitle = getFileTitle(filename)
  const fileExtension = getFileExtension(filename).toUpperCase()

  const onOpenFile = useCallback(() => {
    openFile(getFileUrl(fileId), filename, IS_IOS)
  }, [fileId])

  return (
    <Container>
      <ActionHandler
        state={state}
        percentage={percentage}
        onCancelClick={cancelOperation}
        onDownloadClick={startDownload}
        onCustomActionIconClick={onOpenFile}
        customActionIconComp={
          <FileMessageIcon
            sx={{ color: 'white', width: '24px', height: '24px' }}
          />
        }
        selected={selected}
        selectOnTap={selectOnTap}
      />

      <TextsContainer>
        <Typography
          color="semiBlack"
          variant="caption1"
          sx={{ display: 'block', marginBottom: '8px' }}
          noWrap
        >
          {fileTitle}
        </Typography>

        <Stack direction="row" alignItems="center" sx={{ overflow: 'hidden' }}>
          <FileMetaTypography>{fileExtension}</FileMetaTypography>

          <FileMetaTypography
            sx={{
              color: '#c4c4c4',
              margin: '0 4px',
            }}
          >
            /
          </FileMetaTypography>

          <FileMetaTypography
            noWrap
            sx={{
              color: isInProgress ? 'primary.main' : 'gray',
            }}
          >
            {isInProgress
              ? `${progressedSizeText} از ${fileSizeText}`
              : fileSizeText}
          </FileMetaTypography>
        </Stack>
      </TextsContainer>
    </Container>
  )
}
